import { CheckCircle, Phone, PhoneMissed, PhoneOff, XCircle, type LucideIcon } from 'lucide-react'
import type { BadgeTone } from '../ui/Badge'

/**
 * STATUT ET ISSUE D'UN APPEL → CLÉS DE LIBELLÉ (LOT-NUMÉRO-INCONNU).
 *
 * Une seule table pour le journal des appels (`CallsJournalTable`) et le
 * panneau « Numéro inconnu » : les enums bruts de `schema.prisma`
 * (`CallStatus`, `CallOutcome`) vers les clés partagées `contacts:callStatus.*`
 * et `contacts:outcome.*`. Jamais de libellé fabriqué depuis la valeur de
 * l'enum (`replace('_', ' ')`) : il ne serait traduit nulle part.
 *
 * Le statut est le SEUL porteur de couleur sémantique d'une ligne (LOT-H5/H8) ;
 * la forme de la pilule vit dans `ui/Badge`, seule la table statut → ton est ici.
 */

/** `CallStatus` (schema.prisma) → `contacts:callStatus.<clé>`. */
export const CALL_STATUS_KEYS: Record<string, string> = {
  QUEUED: 'queued',
  DIALING: 'dialing',
  ANSWERED: 'answered',
  IN_PROGRESS: 'inProgress',
  NO_ANSWER: 'noAnswer',
  BUSY: 'busy',
  FAILED: 'failed',
  MISSED: 'missed',
}

/** `CallOutcome` (schema.prisma) → `contacts:outcome.<clé>`. */
export const CALL_OUTCOME_KEYS: Record<string, string> = {
  PENDING: 'pending',
  NO_ANSWER: 'noAnswer',
  INTERESTED: 'interested',
  NOT_INTERESTED: 'notInterested',
  CALLBACK: 'callback',
  ALREADY_DONATED: 'alreadyDonated',
  WRONG_NUMBER: 'wrongNumber',
  VOICEMAIL: 'voicemail',
}

export interface CallStatusBadge {
  icon: LucideIcon
  tone: BadgeTone
}

const STATUS_BADGES: Record<string, CallStatusBadge> = {
  ANSWERED: { icon: CheckCircle, tone: 'success' },
  NO_ANSWER: { icon: PhoneMissed, tone: 'neutral' },
  MISSED: { icon: PhoneMissed, tone: 'neutral' },
  BUSY: { icon: PhoneOff, tone: 'warning' },
  FAILED: { icon: XCircle, tone: 'danger' },
  RINGING: { icon: Phone, tone: 'accent' },
  IN_PROGRESS: { icon: Phone, tone: 'accent' },
}

/** Icône et ton de la pilule d'un statut ; neutre pour tout statut inconnu. */
export function callStatusBadge(status: string): CallStatusBadge {
  return STATUS_BADGES[status] ?? { icon: Phone, tone: 'neutral' }
}
